import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../models/NameUtils';
import {
	useCreateServiceJobMutation,
	useUpdateServiceJobMutation,
	invalidateAllServiceJobFilters,
	invalidateAllServiceJobSummary,
} from '../../../../../queries';
import { CloseButton } from '../../../../components/CloseButton';
import { Modal } from '../../../../components/Modal';
import { SimpleAutoCompleteSearchField } from '../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { Button } from '../../../../components-v2/Button';
import { DateInput } from '../../../../components-v2/DateInput';
import { Input } from '../../../../components-v2/Input';
import { ToolsIcon } from '../../../../components/svgs/icons/ToolsIcon';
import { white } from '../../../../styles/colors';
import { styleSheet } from '../../EditModal/styles';
import { ServiceJobStatusSearch } from '../ServiceJobStatusSearch';

export const EditServiceJobModal = ({
	initialJob,
	isOpen,
	setIsOpen,
	onSave,
	onCancel,
}: {
	initialJob: Partial<Api.IServiceJob>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onSave: (job: Api.IServiceJob) => void;
	onCancel: () => void;
}) => {
	const [contact, setContact] = React.useState<Api.IContact | null>(initialJob.contact ?? null);
	const [jobNumber, setJobNumber] = React.useState(initialJob.jobNumber ?? '');
	const [summary, setSummary] = React.useState(initialJob.summary ?? '');
	const [statusObj, setStatusObj] = React.useState<Api.IServiceJobStatus | null>(initialJob.jobStatus ?? null);
	const [total, setTotal] = React.useState<string>(initialJob.total?.toString() ?? '');
	const [completedOn, setCompletedOn] = React.useState<string>(
		initialJob.completedOn ? moment(initialJob.completedOn).format('YYYY-MM-DD') : ''
	);

	const isEditing = !!initialJob.id;
	const isReadOnly = isEditing && initialJob.canModify === false;

	// State is initialized from initialJob on mount only. Callers pass key={job.id ?? 'new'} so the
	// modal remounts (and re-initializes) when the edited job changes, rather than syncing props into
	// state via an effect — which would clobber in-progress edits on every parent re-render.

	const createJobMutation = useCreateServiceJobMutation({
		onSuccess: (job: Api.IServiceJob) => {
			toast.success('Service job successfully created');
			invalidateAllServiceJobFilters();
			invalidateAllServiceJobSummary();
			onSave(job);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to create service job');
		},
	});

	const updateJobMutation = useUpdateServiceJobMutation({
		onSuccess: (job: Api.IServiceJob) => {
			toast.success('Service job successfully updated');
			invalidateAllServiceJobFilters();
			invalidateAllServiceJobSummary();
			onSave(job);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to update service job');
		},
	});

	const save = () => {
		const updatedJob: Partial<Api.IServiceJob> = {
			...initialJob,
			contactId: contact?.id ?? '',
			jobNumber,
			summary: summary || undefined,
			jobStatusId: statusObj?.id,
			total: total ? parseFloat(total) : undefined,
			completedOn: completedOn || undefined,
		};

		if (isEditing) {
			updateJobMutation.mutateAsync({ job: updatedJob as Api.IServiceJob });
			return;
		}
		createJobMutation.mutateAsync({ job: updatedJob });
	};

	const cancel = () => {
		onCancel();
		setIsOpen(false);
	};

	const onContactCleared = () => {
		setContact(null);
	};

	const onContactSelected = ({ selection }: { selection: Api.IEntity | string | Api.IUser }) => {
		if (typeof selection !== 'string' && 'id' in selection) {
			setContact(selection as Api.IContact);
		}
	};

	const isSaving = createJobMutation.isPending || updateJobMutation.isPending;
	const canSave = !isReadOnly && !!contact && !!jobNumber && !isSaving;

	const onClose = () => {
		setIsOpen(false);
	};

	return (
		<Modal
			className={css(styleSheet.modalContainer)}
			isOpen={isOpen}
			onRequestClose={cancel}
			headerAccessory={
				<div className={css(styleSheet.modalHeader)}>
					<ToolsIcon />
					<CloseButton onClick={onClose} fillColor={white} />
				</div>
			}
		>
			<article className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>
					{isReadOnly ? 'View Service Job' : `${isEditing ? 'Edit' : 'Add New'} Service Job`}
				</h3>

				{isReadOnly && (
					<div className={css(styleSheet.errorText)} style={{ marginBottom: 16 }}>
						This job is managed by an integration and cannot be edited here.
					</div>
				)}

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Contact *</label>
					<div className={css(styleSheet.formControlInputWrap)}>
						<SimpleAutoCompleteSearchField
							disabled={isReadOnly}
							onClear={onContactCleared}
							onItemSelected={onContactSelected}
							resultsLimit={5}
							pageSize={5}
							placeholder='Search for contact...'
							style={styleSheet.contactSearchField}
							type={Api.ResourceAutoCompleteViewModelType.Contact}
							initialSearchQuery={initialJob.contact ? getDisplayName(initialJob.contact) : ''}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Job Number *</label>
					<div className={css(styleSheet.formControlInputWrap)}>
						<Input
							disabled={isReadOnly || isEditing}
							id='service-job-modal-edit-number'
							onChange={e => setJobNumber(e.target.value)}
							type='text'
							value={jobNumber}
							placeholder='Enter job number'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>
						Summary
						<span className={css(styleSheet.optionalLabel)}>(optional)</span>
					</label>
					<div className={css(styleSheet.formControlInputWrap)}>
						<Input
							disabled={isReadOnly}
							id='service-job-modal-edit-summary'
							onChange={e => setSummary(e.target.value)}
							type='text'
							value={summary}
							placeholder='Enter summary'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>
						Status
						<span className={css(styleSheet.optionalLabel)}>(optional)</span>
					</label>
					<div className={css(styleSheet.formControlInputWrap)}>
						<ServiceJobStatusSearch
							onSelectStatus={setStatusObj}
							canAdd={!isReadOnly}
							onCreateStatus={setStatusObj}
							initialSearchFragment={statusObj?.name ?? ''}
							onClear={() => setStatusObj(null)}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>
						Total
						<span className={css(styleSheet.optionalLabel)}>(optional)</span>
					</label>
					<div className={css(styleSheet.formControlInputWrap)}>
						<Input
							disabled={isReadOnly}
							id='service-job-modal-edit-total'
							onChange={e => setTotal(e.target.value)}
							type='number'
							value={total}
							placeholder='0.00'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>
						Completed On
						<span className={css(styleSheet.optionalLabel)}>(optional)</span>
					</label>
					<div className={css(styleSheet.formControlInputWrap)}>
						<DateInput value={completedOn} onChange={setCompletedOn} />
					</div>
				</div>

				<footer className={css(styleSheet.footer)}>
					{!isReadOnly && (
						<Button variant='primary' onClick={save} disabled={!canSave} isLoading={isSaving}>
							Save
						</Button>
					)}
					<Button variant='secondary' onClick={cancel}>
						{isReadOnly ? 'Close' : 'Cancel'}
					</Button>
				</footer>
			</article>
		</Modal>
	);
};
